import {
  buildHandleTransitionCurve,
  deriveDoorGeometry,
  evaluateHandleTransition,
  normalizeDoorConfig,
} from './door-model';
import type { ConfiguratorState, HandleVariant } from './configurator-types';

export type TcnToolpathPoint = Readonly<{ xMm: number; yMm: number }>;

export type TcnToolpathPass = Readonly<{
  id: 'roughing' | 'finishing';
  operationSequence: 34 | 36;
  depthMm: number;
  points: readonly TcnToolpathPoint[];
}>;

export type TcnMachiningPlan = Readonly<{
  variant: HandleVariant;
  stock: Readonly<{ widthMm: number; heightMm: number; thicknessMm: number }>;
  finished: Readonly<{ widthMm: number; heightMm: number; thicknessMm: number }>;
  datum: 'finished-origin-lower-left-stock-allowance-positive';
  leadYMm: number;
  toolId: 1009;
  macro: '..\\custom\\mcr\\fresatebarnesting.tmcr';
  passes: readonly [TcnToolpathPass, TcnToolpathPass];
}>;

/**
 * Values that belong to the supplied machine template rather than the product preview.
 * Tool 1009 needs to be measured on the target machine before this template is released.
 */
export const TCN_CAM_TEMPLATE = {
  toolId: 1009,
  macro: '..\\custom\\mcr\\fresatebarnesting.tmcr',
  openEdgeOverrunMm: 50,
  // Calibrated from the supplied program at the visible cap X=141 mm:
  // W#89 starts at X=172.5 (roughing) and X=170.5 (finishing).
  roughVisibleToToolCentreXMm: 31.5,
  finishVisibleToToolCentreXMm: 29.5,
  finishTrackInsetCorrectionMm: 1,
  roughTrackInsetCorrectionMm: 2,
  roughTransitionLengthCorrectionMm: 1,
  finishOpenExitGuideBacktrackMm: 38,
  terminalTangentGuideMm: 50,
  transitionChordToleranceMm: 0.05,
  maxTransitionSubdivisions: 12,
} as const;

export type TcnToolEnvelope = Readonly<{
  capCount: 0 | 1 | 2;
  requiredShiftPerCapMm: number;
  availableShiftPerCapMm: number;
  fits: boolean;
}>;

export function inspectTcnToolEnvelope(config: ConfiguratorState): TcnToolEnvelope {
  return inspectToolEnvelopeGeometry(deriveDoorGeometry(normalizeDoorConfig(config)));
}

/**
 * Converts the canonical product geometry into an explicit centre-line program.
 *
 * The source file exposes only one machine/tool combination. Consequently the tool
 * calibration is kept here, separate from the visible 3D model. The datum assumption
 * is explicit: the finished lower-left corner is X0/Y0 and stock allowances lie in
 * positive X/Y/Z. The generated TCN remains subject to CAM simulation and tool-table
 * verification, but unlike the former stub it contains a complete path for every valid
 * product variant.
 */
export function buildTcnMachiningPlan(config: ConfiguratorState): TcnMachiningPlan {
  const canonical = normalizeDoorConfig(config);
  const geometry = deriveDoorGeometry(canonical);
  const envelope = inspectToolEnvelopeGeometry(geometry);
  if (!envelope.fits) {
    throw new RangeError(
      `Tool 1009 envelope needs ${envelope.requiredShiftPerCapMm} mm per cap, only ${envelope.availableShiftPerCapMm} mm is available.`,
    );
  }
  const finishingDepthMm = canonical.handle.depthMm;
  const roughingDepthMm = Math.max(0, finishingDepthMm - canonical.handle.roughingAllowanceMm);

  const roughing = buildPass(canonical, 'roughing', 34, roughingDepthMm);
  const finishing = buildPass(canonical, 'finishing', 36, finishingDepthMm);

  return {
    variant: geometry.variant,
    stock: geometry.stock,
    finished: geometry.finished,
    datum: 'finished-origin-lower-left-stock-allowance-positive',
    leadYMm: geometry.stock.heightMm + geometry.handle.faceWidthMm,
    toolId: TCN_CAM_TEMPLATE.toolId,
    macro: TCN_CAM_TEMPLATE.macro,
    passes: [roughing, finishing],
  };
}

function buildPass(
  config: ConfiguratorState,
  id: TcnToolpathPass['id'],
  operationSequence: TcnToolpathPass['operationSequence'],
  depthMm: number,
): TcnToolpathPass {
  const geometry = deriveDoorGeometry(config);
  const isRoughing = id === 'roughing';
  const topY = geometry.finished.heightMm;
  const trackCorrection = isRoughing
    ? TCN_CAM_TEMPLATE.roughTrackInsetCorrectionMm
    : TCN_CAM_TEMPLATE.finishTrackInsetCorrectionMm;
  const trackInsetMm = Math.max(0.5, geometry.handle.grooveWidthMm - trackCorrection);
  const trackY = topY - trackInsetMm;
  const leadY = geometry.stock.heightMm + geometry.handle.faceWidthMm;
  const requestedToolShiftMm = isRoughing
    ? TCN_CAM_TEMPLATE.roughVisibleToToolCentreXMm
    : TCN_CAM_TEMPLATE.finishVisibleToToolCentreXMm;
  const toolShiftMm = requestedToolShiftMm;

  const points: TcnToolpathPoint[] = [];

  if (geometry.handle.capLeft) {
    const entryX = geometry.handle.startMm + toolShiftMm;
    const trackX =
      entryX +
      Math.max(
        1,
        geometry.handle.transitionLengthMm -
          (isRoughing ? TCN_CAM_TEMPLATE.roughTransitionLengthCorrectionMm : 0),
      );
    points.push(
      ...sampleCap(
        { xMm: entryX, yMm: leadY },
        { xMm: trackX, yMm: trackY },
        geometry.handle.transitionRoundness,
      ),
    );
  } else {
    points.push({ xMm: -TCN_CAM_TEMPLATE.openEdgeOverrunMm, yMm: trackY });
  }

  if (geometry.handle.capRight) {
    const exitX = geometry.handle.endMm - toolShiftMm;
    const trackX =
      exitX -
      Math.max(
        1,
        geometry.handle.transitionLengthMm -
          (isRoughing ? TCN_CAM_TEMPLATE.roughTransitionLengthCorrectionMm : 0),
      );
    const rightCap = sampleCap(
      { xMm: exitX, yMm: leadY },
      { xMm: trackX, yMm: trackY },
      geometry.handle.transitionRoundness,
    ).reverse();
    appendDistinct(points, rightCap);
  } else {
    appendDistinct(points, [
      {
        xMm: geometry.stock.widthMm + TCN_CAM_TEMPLATE.openEdgeOverrunMm,
        yMm: trackY,
      },
    ]);
  }

  return {
    id,
    operationSequence,
    depthMm: roundTcnNumber(depthMm),
    points: points.map(point => ({
      xMm: roundTcnNumber(point.xMm),
      yMm: roundTcnNumber(point.yMm),
    })),
  };
}

function sampleCap(start: TcnToolpathPoint, end: TcnToolpathPoint, roundnessPercent: number) {
  const curve = buildHandleTransitionCurve(
    { x: start.xMm, y: start.yMm },
    { x: end.xMm, y: end.yMm },
    roundnessPercent,
  );
  return sampleTransitionAdaptive(curve).map(point => ({
    xMm: point.x,
    yMm: point.y,
  }));
}

function inspectToolEnvelopeGeometry(
  geometry: ReturnType<typeof deriveDoorGeometry>,
): TcnToolEnvelope {
  const capCount = (Number(geometry.handle.capLeft) + Number(geometry.handle.capRight)) as
    | 0
    | 1
    | 2;
  if (capCount === 0) {
    return {
      capCount,
      requiredShiftPerCapMm: 0,
      availableShiftPerCapMm: 0,
      fits: true,
    };
  }
  const transitionSpace = geometry.handle.transitionLengthMm * capCount;
  const flatSpace = Math.max(0, geometry.handle.lengthMm - transitionSpace);
  const availableShiftPerCapMm = flatSpace / capCount;
  return {
    capCount,
    requiredShiftPerCapMm: TCN_CAM_TEMPLATE.roughVisibleToToolCentreXMm,
    availableShiftPerCapMm: roundTcnNumber(availableShiftPerCapMm),
    fits: availableShiftPerCapMm >= TCN_CAM_TEMPLATE.roughVisibleToToolCentreXMm,
  };
}

function sampleTransitionAdaptive(
  curve: ReturnType<typeof buildHandleTransitionCurve>,
): Array<{ x: number; y: number }> {
  const start = evaluateHandleTransition(curve, 0);
  const end = evaluateHandleTransition(curve, 1);
  const points = [start];

  const subdivide = (
    startT: number,
    startPoint: { x: number; y: number },
    endT: number,
    endPoint: { x: number; y: number },
    depth: number,
  ) => {
    const middleT = (startT + endT) / 2;
    const firstQuarterT = (startT * 3 + endT) / 4;
    const thirdQuarterT = (startT + endT * 3) / 4;
    const middlePoint = evaluateHandleTransition(curve, middleT);
    const chordErrorMm = Math.max(
      pointToSegmentDistance(evaluateHandleTransition(curve, firstQuarterT), startPoint, endPoint),
      pointToSegmentDistance(middlePoint, startPoint, endPoint),
      pointToSegmentDistance(evaluateHandleTransition(curve, thirdQuarterT), startPoint, endPoint),
    );
    if (
      chordErrorMm <= TCN_CAM_TEMPLATE.transitionChordToleranceMm ||
      depth >= TCN_CAM_TEMPLATE.maxTransitionSubdivisions
    ) {
      points.push(endPoint);
      return;
    }
    subdivide(startT, startPoint, middleT, middlePoint, depth + 1);
    subdivide(middleT, middlePoint, endT, endPoint, depth + 1);
  };

  subdivide(0, start, 1, end, 0);
  return points;
}

function pointToSegmentDistance(
  point: { x: number; y: number },
  start: { x: number; y: number },
  end: { x: number; y: number },
) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(point.x - start.x, point.y - start.y);
  const progress = Math.max(
    0,
    Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared),
  );
  const projectedX = start.x + progress * dx;
  const projectedY = start.y + progress * dy;
  return Math.hypot(point.x - projectedX, point.y - projectedY);
}

function appendDistinct(target: TcnToolpathPoint[], points: readonly TcnToolpathPoint[]) {
  for (const point of points) {
    const previous = target.at(-1);
    if (
      previous &&
      Math.abs(previous.xMm - point.xMm) < 0.0005 &&
      Math.abs(previous.yMm - point.yMm) < 0.0005
    ) {
      continue;
    }
    target.push(point);
  }
}

function roundTcnNumber(value: number) {
  return Math.round(value * 1_000) / 1_000;
}
